<template>
  <section
    class="-card-view"
    :class="{ '-expanded': isExpanded(expandContentId) }"
    @mouseenter="expandCard"
    @mouseleave="collapseCard"
  >
    <div
      class="card-content"
      :class="{
        '-with-extras': isPlanModCta,
      }"
    >
      <component :is="isPlanModCta ? 'div' : 'a'" :id="'video-' + video.id" :href="detailUrl">
        <div class="content-group -content-asset">
          <div class="group responsive-media">
            <card-src-set-image
              type="card-640"
              :img-prefix="imageUrlPrefix"
              :image="video.image"
              :title="video.title"
            />
          </div>
          <card-icon-bar
            :is-exclusive="!!video.is_exclusive"
            :is-plus-free-access="!!(video.is_exclusive && plusFreeAccess.enabled)"
            :entity-type="entityType"
            :entity-id="video.id"
          />
        </div>
      </component>

      <div class="content-group -content-summary">
        <div class="group">
          <div class="summary-group">
            <component :is="isPlanModCta ? 'div' : 'a'" :href="detailUrl">
              <h1 class="content-title">
                {{ video.title }}
                <span v-if="video.subtitle" class="sub-title"> <br />{{ video.subtitle }} </span>
              </h1>
              <div class="primary-detail" data-tour="workout-minutes">
                {{ video.minutes }} Min &bull; {{ bodyFocus }}

                <span v-if="vibe" class="-hidden"> &bull; {{ vibe }} </span>
              </div>
            </component>
          </div>
        </div>
      </div>

      <!-- only if in calendar day swap workout -->
      <div v-if="isPlanModCta" class="content-group -content-extras">
        <div class="card-cta">
          <button
            class="btn -main"
            :disabled="cardCtaDisabled ? true : undefined"
            @click="planModAction"
          >
            Swap
          </button>
        </div>
      </div>

      <!-- expander -->
      <div v-if="showExpander" class="content-group -content-details">
        <div class="more-info">
          <expand-stats-toggle :expand-content-id="expandContentId" data-tour="workout-expand" />
          <expand-stats-content :expand-content-id="expandContentId">
            <a :href="detailUrl">
              <div class="detail-group">
                <div class="secondary-details">
                  <ul class="detail-list">
                    <difficulty-rating
                      tag-is="li"
                      :difficulty="video.difficulty"
                    ></difficulty-rating>
                    <li>
                      <span class="detail-title"> Training Type </span>
                      <span class="detail-value">
                        {{ trainingType }}
                      </span>
                    </li>
                    <li>
                      <span class="detail-title"> Equipment </span>
                      <span class="detail-value">
                        {{ equipment }}
                      </span>
                    </li>
                    <burn-estimate
                      tag-is="li"
                      :burn-min="video.burnmin"
                      :burn-max="video.burnmax"
                    ></burn-estimate>
                  </ul>
                </div>
              </div>

              <!-- stats -->
              <div class="stats-group">
                <div class="content-stats">
                  <!-- user -->
                  <stats-member
                    :show-user-complete-count="true"
                    :show-favorite="true"
                    :entity-id="video.id"
                    :entity-type="entityType"
                  ></stats-member>

                  <!-- community -->
                  <stats-community
                    :complete-count="video.complete_count"
                    :comment-count="video.comment_count"
                    :favorite-count="video.favorite_count"
                  ></stats-community>
                </div>
              </div>
            </a>
          </expand-stats-content>
        </div>
      </div>
    </div>

    <!-- action buttons -->
    <div v-if="!showCardCta" class="card-actions">
      <div class="action-options">
        <div class="schedule-action" :data-tour="isSchedulable ? 'add-to-calendar' : false">
          <calendar-button
            :entity-id="video.id"
            :entity-type="entityType"
            :entity-title="video.title"
            :is-schedulable="isSchedulable"
            :is-exclusive="isExclusive"
            :plus-pass="plusPass"
            :cheapest-monthly-price="cheapestMonthlyPrice"
            :image-url-prefix="imageUrlPrefix"
            :image="video.image"
          ></calendar-button>
        </div>

        <content-actions-button
          :entity-id="video.id"
          :entity-type="entityType"
          :entity-title="video.title"
          :favorite-button="true"
          :tags-button="features.TAGS"
          :similar-button="features.SIMILAR_FILTERS"
          :add-to-routine-button="features.ROUTINES"
          data-tour="workout-actions"
        />
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import type { PropType } from 'vue'
import { mapState, mapActions } from 'pinia'
import { useUserAuthStore } from '@/stores/user-auth'
import { useExpandersStore } from '@/stores/expanders'
import CardSrcSetImage from '@/components/images/card-src-set-image.vue'
import ExpandStatsContent from '@/components/cards/expand-stats-content.vue'
import ExpandStatsToggle from '@/components/cards/expand-stats-toggle.vue'
import CalendarButton from '@/components/buttons/calendar-button.vue'
import ContentActionsButton from '@/components/buttons/content-actions-button.vue'
import StatsCommunity from '@/components/cards/stats-community.vue'
import StatsMember from '@/components/cards/stats-member.vue'
import BurnEstimate from '@/components/videos/burn-estimate.vue'
import DifficultyRating from '@/components/videos/difficulty-rating.vue'
import CardIconBar from '@/components/cards/card-icon-bar.vue'
import type { PlusPass } from '@/types/search-types'

export default defineComponent({
  name: 'VideoCard',
  components: {
    CardSrcSetImage,
    ExpandStatsContent,
    ExpandStatsToggle,
    CalendarButton,
    ContentActionsButton,
    BurnEstimate,
    DifficultyRating,
    StatsCommunity,
    StatsMember,
    CardIconBar,
  },
  props: {
    video: {
      type: Object as any, // TODO: ct use Video type
      required: true,
    },
    plusPass: {
      type: Object as PropType<PlusPass | null>,
      default: null,
    },
    cheapestMonthlyPrice: {
      type: String,
      default: '',
    },
    showExpander: {
      type: Boolean,
      default: true,
    },
    showCardCta: {
      type: String,
      default: '',
      validator: (val: string) => ['', 'planModCta'].includes(val),
    },
    cardCtaDisabled: {
      type: Boolean,
      default: false,
    },
    // use if two cards can be on the same page, to avoid both expanding
    // since powered by pinia
    customExpanderKey: {
      type: String,
      default: '',
    },
  },
  emits: ['mod-select'],
  data() {
    return {}
  },
  computed: {
    ...mapState(useUserAuthStore, ['features', 'device', 'user', 'cdn', 'plusFreeAccess']),
    ...mapState(useExpandersStore, ['isExpanded']),

    // disable anchor links and show CTA for program swap
    isPlanModCta() {
      return this.showCardCta === 'planModCta'
    },

    detailUrl() {
      return `/videos/${this.video.seo_url}`
    },

    imageUrlPrefix() {
      // get siteassets url from page load, allows CDN url to be added
      const siteAssetsPrefix = this.cdn.siteAssetsPrefix || '/siteassets'
      return `${siteAssetsPrefix}/${this.video.img_dir}`
    },

    entityType() {
      return 'workout'
    },

    isExclusive() {
      return !!this.video.is_exclusive
    },

    isSchedulable() {
      // anon, show but will prompt for sign upo
      if (!this.user.isLoggedIn) {
        return false
      }

      // free
      if (!this.isExclusive) {
        return true
      }

      // exclusive access check
      if (this.isExclusive && this.features.EXCLUSIVE_CONTENT) {
        return true
      }

      // default to false
      return false
    },

    // could be multiple in db, but only show first match
    bodyFocus() {
      // elastic search has categories set, just return first in list
      if (this.video && Array.isArray(this.video.taxonomy)) {
        return this.video.taxonomy
          .filter((item: any) => item.category === 'bodyfocus')
          .map((item: any) => item.name)
          .join(', ')
      }

      return ''
    },

    // could be multiple in db, but only show first match
    equipment() {
      // elastic search has categories set, just return first in list
      if (this.video && Array.isArray(this.video.taxonomy)) {
        return this.video.taxonomy
          .filter((item: any) => item.category === 'equipment')
          .map((item: any) => item.name)
          .join(', ')
      }

      return ''
    },

    // could be multiple in db, but only show first match
    trainingType() {
      // elastic search has categories set, just return first in list
      if (this.video && Array.isArray(this.video.taxonomy)) {
        return this.video.taxonomy
          .filter((item: any) => item.category === 'trainingtype')
          .map((item: any) => item.name)
          .join(', ')
      }

      return ''
    },

    // could be multiple in db, but only show first match
    vibe() {
      // elastic search has categories set, just return first in list
      if (this.video && Array.isArray(this.video.taxonomy)) {
        return this.video.taxonomy
          .filter((item: any) => item.category === 'vibe')
          .map((item: any) => item.name)
          .join(', ')
      }

      return ''
    },

    expandContentId(): string {
      if (this.customExpanderKey) {
        return `video-stats-${this.customExpanderKey}-${this.video.id}`
      }

      return `video-stats-${this.video.id}`
    },
  },
  methods: {
    ...mapActions(useExpandersStore, ['setExpanded']),

    expandCard() {
      if (this.showExpander && this.device.pointerType === 'fine') {
        this.setExpanded({
          id: this.expandContentId,
          expanded: true,
          collapseAll: true,
        })
      }
    },

    collapseCard() {
      if (this.showExpander && this.device.pointerType === 'fine') {
        this.setExpanded({
          id: this.expandContentId,
          expanded: false,
          collapseAll: false,
        })
      }
    },

    planModAction() {
      this.$emit('mod-select', {
        schedulableType: 'workout',
        schedulableId: this.video.id,
      })
    },
  },
})
</script>
